import { useState, useEffect } from 'react';
import { database } from '../services/firebase';
import { ref, onValue } from 'firebase/database';
import { subscribeToFriends } from '../services/friendService';
import ChatWindow from './ChatWindow';
import '../styles/MessagingCenter.css';

// Helper function to safely get a string value (prevents rendering objects)
const safeString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
};

const MessagingCenter = ({ currentUser }) => {
  const [showPanel, setShowPanel] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [friends, setFriends] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Subscribe to user's conversations
  useEffect(() => {
    if (!currentUser) return;

    const conversationsRef = ref(database, `conversations/${currentUser.uid}`);
    const unsubscribe = onValue(conversationsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const convList = Object.entries(data).map(([otherUserId, convData]) => ({
          userId: otherUserId,
          ...convData
        }));

        // Sort by last message timestamp
        convList.sort((a, b) => (b.lastMessageTime || 0) - (a.lastMessageTime || 0));

        setConversations(convList);

        // Count unread messages
        const unread = convList.reduce((count, conv) => count + (conv.unreadCount || 0), 0);
        setUnreadCount(unread);
      } else {
        setConversations([]);
        setUnreadCount(0);
      }
    }, (error) => {
      console.error('Error loading conversations:', error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Subscribe to friends list
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = subscribeToFriends(currentUser.uid, (friendsList) => {
      setFriends(friendsList);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleOpenChat = (conversation) => {
    setActiveChat({
      userId: conversation.userId,
      displayName: conversation.displayName,
      photoURL: conversation.photoURL
    });
    setShowPanel(false);
    setShowNewMessage(false);
  };

  const handleStartNewChat = (friend) => {
    setActiveChat({
      userId: friend.userId,
      displayName: friend.displayName,
      photoURL: friend.photoURL
    });
    setShowPanel(false);
    setShowNewMessage(false);
  };

  const handleCloseChat = () => {
    setActiveChat(null);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';

    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <>
      <div className="messaging-center">
        {/* Floating Chat Button - Hide when chat is open */}
        {!activeChat && (
          <button
            className={`chat-button ${unreadCount > 0 ? 'has-unread' : ''}`}
            onClick={() => setShowPanel(!showPanel)}
            title="Messages"
          >
            <span className="chat-icon">💬</span>
            {unreadCount > 0 && (
              <span className="chat-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>
        )}

        {/* Conversations Panel */}
        {showPanel && !showNewMessage && (
          <div className="conversations-panel">
            <div className="conversations-header">
              <h3>Messages {conversations.length > 0 && `(${conversations.length})`}</h3>
              <div className="header-actions">
                <button
                  className="new-message-btn"
                  onClick={() => setShowNewMessage(true)}
                  title="New Message"
                >
                  ✏️
                </button>
                <button
                  className="close-panel-btn"
                  onClick={() => setShowPanel(false)}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="conversations-list">
              {conversations.length === 0 ? (
                <div className="no-conversations">
                  <span className="no-conversations-icon">💬</span>
                  <p>No messages yet</p>
                  <small>Start chatting with friends!</small>
                </div>
              ) : (
                conversations.map((conversation) => (
                  <div
                    key={conversation.userId}
                    className={`conversation-item ${conversation.unreadCount > 0 ? 'unread' : ''}`}
                    onClick={() => handleOpenChat(conversation)}
                  >
                    <div className="conversation-avatar-wrapper">
                      <img
                        src={safeString(conversation?.photoURL, '')}
                        alt={safeString(conversation?.displayName, 'User')}
                        className="conversation-avatar"
                      />
                      {conversation.unreadCount > 0 && (
                        <span className="unread-dot"></span>
                      )}
                    </div>
                    <div className="conversation-info">
                      <div className="conversation-header-row">
                        <h4 className="conversation-name">
                          {conversation.unreadCount > 0 && <span className="unread-icon">🔔</span>}
                          {safeString(conversation?.displayName, 'User')}
                        </h4>
                        <span className="conversation-time">
                          {formatTime(conversation?.lastMessageTime)}
                        </span>
                      </div>
                      <p className="conversation-preview">
                        {safeString(conversation?.lastMessage, 'No messages yet')}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* New Message Panel */}
        {showPanel && showNewMessage && (
          <div className="conversations-panel">
            <div className="conversations-header">
              <button
                className="back-btn"
                onClick={() => setShowNewMessage(false)}
                title="Back"
              >
                ← Back
              </button>
              <h3>New Message</h3>
              <button
                className="close-panel-btn"
                onClick={() => {
                  setShowPanel(false);
                  setShowNewMessage(false);
                }}
              >
                ✕
              </button>
            </div>

            <div className="conversations-list">
              {friends.length === 0 ? (
                <div className="no-conversations">
                  <span className="no-conversations-icon">👥</span>
                  <p>No friends yet</p>
                  <small>Add friends to start chatting!</small>
                </div>
              ) : (
                friends.map((friend) => (
                  <div
                    key={friend.userId}
                    className="conversation-item friend-item"
                    onClick={() => handleStartNewChat(friend)}
                  >
                    <div className="conversation-avatar-wrapper">
                      <img
                        src={safeString(friend?.photoURL, '')}
                        alt={safeString(friend?.displayName, 'Friend')}
                        className="conversation-avatar"
                      />
                    </div>
                    <div className="conversation-info">
                      <h4 className="conversation-name">{safeString(friend?.displayName, 'Friend')}</h4>
                      <p className="conversation-preview">Send a message</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Active Chat Window */}
      {activeChat && (
        <div className="floating-chat-window">
          <ChatWindow
            currentUser={currentUser}
            otherUser={activeChat}
            onClose={handleCloseChat}
            isFloating={true}
          />
        </div>
      )}
    </>
  );
};

export default MessagingCenter;
